import React from 'react';
import { Phone, MessageCircle, MapPin, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getAllCities } from '@/data/cityData';
import SEOHead from '@/components/SEOHead';

const AreasPage = () => {
  const cityPages = getAllCities();
  
  const washingtonCities = [
    'Everett', 'Marysville', 'Lake Stevens', 'Snohomish', 'Arlington', 'Stanwood',
    'Bainbridge Island', 'Mount Vernon', 'Anacortes', 'Oak Harbor', 'Burlington', 
    'Sedro-Woolley', 'Lynden', 'Ferndale', 'Blaine', 'Bothell', 'Lynnwood', 
    'Mukilteo', 'Lake Forest Park', 'Kenmore', 'Woodinville', 'Issaquah', 
    'Mercer Island', 'SeaTac', 'Tukwila', 'Des Moines', 'Puyallup', 
    'University Place', 'Gig Harbor', 'Port Orchard', 'Bremerton', 'Silverdale', 
    'Poulsbo', 'Sequim', 'Port Angeles', 'Port Townsend', 'Wenatchee', 
    'Ellensburg', 'Walla Walla', 'Pullman', 'Moses Lake'
  ];

  const alaskaCities = [
    'Anchorage', 'Juneau', 'Fairbanks', 'Sitka', 'Ketchikan',
    'Kodiak', 'Bethel', 'Wasilla', 'Palmer', 'Valdez',
    'Seward', 'Homer', 'Soldotna', 'Kenai', 'Cordova',
    'Petersburg', 'Wrangell', 'Dutch Harbor', 'Unalaska'
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="areas-page">
      <SEOHead 
        title="Areas We Serve | Personal Injury Attorney | Washington & Alaska"
        description="Amy C. Brown serves personal injury clients throughout Washington State and Alaska. From Seattle, Tacoma, Spokane to Bellingham - car accidents, maritime injuries, slip and falls. Free consultation."
        pageType="service"
      />
      {/* Hero Section */}
      <section className="pt-36 pb-16 md:pt-40 md:pb-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-3xl">
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-secondary mb-6">
              Areas I Serve
            </h1>
            <p className="text-xl text-slate-600 mb-8 leading-relaxed">
              I handle injury cases from Bellingham to Vancouver, Bainbridge Island to Spokane, and all cities in between. Based in Everett, Washington, I serve clients throughout Washington State and Alaska.
            </p>
            <div className="flex flex-wrap gap-4">
              <a 
                href="tel:425-675-1224"
                className="inline-flex items-center gap-2 bg-primary text-white px-8 py-4 rounded-full font-semibold hover:bg-primary/90 transition-all duration-300"
                data-testid="areas-hero-call"
              >
                <Phone className="w-5 h-5" />
                Call (425) 675-1224
              </a>
              <a 
                href="sms:425-675-1224"
                className="inline-flex items-center gap-2 border-2 border-secondary text-secondary px-8 py-4 rounded-full font-semibold hover:bg-secondary hover:text-white transition-all duration-300"
              >
                <MessageCircle className="w-5 h-5" />
                Text Me
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Washington State */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="flex items-center gap-4 mb-8">
            <MapPin className="w-10 h-10 text-teal-600" />
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary">
              Washington State
            </h2>
          </div>
          <p className="text-lg text-slate-600 mb-10 max-w-3xl">
            As an Everett-based personal injury attorney, I proudly serve clients throughout Washington State. Whether you're in the Seattle metro area, the islands, or Eastern Washington, I'm here to help.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {washingtonCities.map((city, index) => (
              <div 
                key={index}
                className="bg-slate-50 rounded-lg px-4 py-3 text-center text-slate-700 hover:bg-teal-50 hover:text-teal-700 transition-colors"
              >
                {city}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Alaska */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="flex items-center gap-4 mb-8">
            <MapPin className="w-10 h-10 text-teal-600" />
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary">
              Alaska
            </h2>
          </div>
          <p className="text-lg text-slate-600 mb-10 max-w-3xl">
            I specialize in maritime injury cases in Washington and Alaska, helping injured workers secure compensation for medical costs, lost wages, and pain and suffering. My experience working on vessels in Southeast Alaska gives me unique insight into the maritime industry.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {alaskaCities.map((city, index) => (
              <div 
                key={index}
                className="bg-white rounded-lg px-4 py-3 text-center text-slate-700 hover:bg-teal-50 hover:text-teal-700 transition-colors shadow-sm"
              >
                {city}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* City-Specific Landing Pages */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary mb-4">
              Personal Injury Lawyer Near You
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Detailed information about personal injury cases in specific Washington cities I serve. Click your city for local resources and FAQs.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {cityPages.map((city) => (
              <Link 
                key={city.slug}
                to={`/service-area/${city.slug}`}
                className="group bg-slate-50 rounded-xl p-5 hover:bg-teal-50 hover:shadow-lg transition-all border border-slate-100"
                data-testid={`city-link-${city.slug}`}
              >
                <div className="flex items-center gap-3 mb-2">
                  <MapPin className="w-5 h-5 text-teal-600 flex-shrink-0" />
                  <h3 className="font-heading text-lg font-semibold text-secondary group-hover:text-teal-700">{city.name}</h3>
                </div>
                <p className="text-slate-500 text-sm">{city.county}</p>
                <span className="inline-flex items-center gap-1 text-teal-600 font-medium text-sm mt-3 group-hover:gap-2 transition-all">
                  View local info <ChevronRight className="w-4 h-4" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Legacy Slip and Fall Pages */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-secondary mb-3">
              Slip and Fall Case Resources
            </h2>
            <p className="text-slate-600 max-w-xl mx-auto">
              In-depth guides for premises liability cases in key cities.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <Link 
              to="/everett-slip-and-fall-lawyer"
              className="group bg-white rounded-2xl p-6 hover:bg-teal-50 hover:shadow-lg transition-all border border-slate-100"
            >
              <div className="flex items-center gap-3 mb-3">
                <MapPin className="w-6 h-6 text-teal-600" />
                <h3 className="font-heading text-xl font-semibold text-secondary group-hover:text-teal-700">Everett</h3>
              </div>
              <p className="text-slate-600 text-sm mb-4">Slip and fall cases in Snohomish County, including Alderwood Mall, Boeing areas, and the port.</p>
              <span className="inline-flex items-center gap-1 text-teal-600 font-medium text-sm group-hover:gap-2 transition-all">
                Learn more <ChevronRight className="w-4 h-4" />
              </span>
            </Link>

            <Link 
              to="/tacoma-slip-and-fall-lawyer"
              className="group bg-white rounded-2xl p-6 hover:bg-teal-50 hover:shadow-lg transition-all border border-slate-100"
            >
              <div className="flex items-center gap-3 mb-3">
                <MapPin className="w-6 h-6 text-teal-600" />
                <h3 className="font-heading text-xl font-semibold text-secondary group-hover:text-teal-700">Tacoma</h3>
              </div>
              <p className="text-slate-600 text-sm mb-4">Premises liability cases in Pierce County, including Tacoma Mall and University Place.</p>
              <span className="inline-flex items-center gap-1 text-teal-600 font-medium text-sm group-hover:gap-2 transition-all">
                Learn more <ChevronRight className="w-4 h-4" />
              </span>
            </Link>

            <Link 
              to="/bellingham-slip-and-fall-lawyer"
              className="group bg-white rounded-2xl p-6 hover:bg-teal-50 hover:shadow-lg transition-all border border-slate-100"
            >
              <div className="flex items-center gap-3 mb-3">
                <MapPin className="w-6 h-6 text-teal-600" />
                <h3 className="font-heading text-xl font-semibold text-secondary group-hover:text-teal-700">Bellingham</h3>
              </div>
              <p className="text-slate-600 text-sm mb-4">Whatcom County slip and falls, including Fairhaven, waterfront, and maritime areas.</p>
              <span className="inline-flex items-center gap-1 text-teal-600 font-medium text-sm group-hover:gap-2 transition-all">
                Learn more <ChevronRight className="w-4 h-4" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* Maritime Focus */}
      <section className="py-20 bg-teal-600 text-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
                Maritime Law Expertise Across the Pacific Northwest
              </h2>
              <p className="text-lg text-teal-100 leading-relaxed mb-6">
                Growing up in the Puget Sound and working on vessels in Southeast Alaska and Mexico's Sea of Cortez, I developed a deep connection to the maritime world. This background, combined with my legal experience defending maritime employers, makes me uniquely qualified to represent injured maritime workers.
              </p>
              <p className="text-lg text-teal-100 leading-relaxed mb-8">
                Whether you were injured on a fishing vessel in Alaska, a ferry in Puget Sound, or a cargo ship anywhere in the Pacific Northwest, I understand the unique challenges you face and the laws that protect you.
              </p>
              <Link 
                to="/maritime-injuries"
                className="inline-flex items-center gap-2 bg-white text-teal-700 px-8 py-4 rounded-full font-semibold hover:bg-teal-50 transition-all duration-300"
              >
                Learn About Maritime Injuries
                <ChevronRight className="w-5 h-5" />
              </Link>
            </div>
            <div className="bg-white/10 rounded-2xl p-8 backdrop-blur-sm">
              <h3 className="font-heading text-xl font-semibold mb-6">Practice Areas</h3>
              <ul className="space-y-4">
                {[
                  { name: 'Maritime Injuries', path: '/maritime-injuries' },
                  { name: 'Car Crashes', path: '/car-crashes' },
                  { name: 'First Responder Crashes', path: '/first-responder-crashes' },
                  { name: 'Motorcycle Crashes', path: '/motorcycle-crashes' },
                  { name: 'Slip and Falls', path: '/slip-and-falls' },
                ].map((area) => (
                  <li key={area.path}>
                    <Link 
                      to={area.path}
                      className="flex items-center gap-3 text-white hover:text-teal-200 transition-colors"
                    >
                      <ChevronRight className="w-5 h-5" />
                      {area.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-secondary text-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
            No Matter Where You Are, I'm Here to Help
          </h2>
          <p className="text-xl text-slate-300 mb-8">
            Free consultation - I personally respond to every inquiry within 1 business day.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:425-675-1224"
              className="inline-flex items-center justify-center gap-2 bg-teal-500 text-white px-10 py-5 rounded-full font-bold text-xl hover:bg-teal-600 transition-all duration-300"
              data-testid="areas-cta-call"
            >
              <Phone className="w-6 h-6" />
              Call (425) 675-1224
            </a>
            <a 
              href="sms:425-675-1224"
              className="inline-flex items-center justify-center gap-2 border-2 border-white text-white px-10 py-5 rounded-full font-bold text-xl hover:bg-white/10 transition-all duration-300"
            >
              <MessageCircle className="w-6 h-6" />
              Text (425) 675-1224
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AreasPage;
